import type { AccessMode, AccessModeChoice } from "@/shared/api/types";
import type { StatePresentation } from "./labels";

/**
 * M13 §1 (ADR-0016 decision 2): a person's row on a terminal is decided one of four ways, and the
 * screen has to say WHICH — "off" because somebody switched it off is a different fact from "off"
 * because no group grants it, and only one of them is fixed by adding them to a group.
 *
 * The fourth way is `pass_only` (ADR-0016's addendum of 2026-09-17, server migration 0040) and it is
 * the reason this file changed at all: the row is not on the terminal AND is not a deny. Until it had
 * a case of its own the chip fell through to the `auto` branch and, because such a row is always
 * `enabled: false`, told the operator "Off · no group grants it" — a sentence that says the person is
 * barred and that no group membership could ever fix. Every QR-only visitor was in that state.
 *
 * The wording is shared rather than written per screen: the person's page, the device page's people
 * list and the mobile app all answer the same question, and a reader who learns the sentence on one
 * screen must not have to learn it again on the next (design-system §6).
 */
export const ACCESS_MODE_NAMES: Record<AccessMode, string> = {
  auto: "Follow groups",
  on: "On",
  off: "Off",
  pass_only: "Pass only",
};

/**
 * The three an operator may ask for, in the order the picker shows them. **Not** a `Record<AccessMode,
 * …>` and never four entries long: `pass_only` is written by the server's `holdPersonOffTerminals` and
 * refused by `setAccessBody`, so offering it would be a button that always 422s. The array is typed
 * `AccessModeChoice` rather than `AccessMode` so that this is a statement the compiler checks, not an
 * omission a future reader "fixes" by adding the fourth.
 */
export const ACCESS_MODE_OPTIONS: readonly { value: AccessModeChoice; label: string }[] = (
  ["auto", "on", "off"] as const
).map((value) => ({ value, label: ACCESS_MODE_NAMES[value] }));

/** Whether a mode is one of the three a control may offer — the question every mode picker must ask first. */
export function isAccessModeChoice(mode: AccessMode): mode is AccessModeChoice {
  return ACCESS_MODE_OPTIONS.some((option) => option.value === mode);
}

/** What each mode means, spelled out: under the control for the three set here, in a tooltip for the fourth. */
export const ACCESS_MODE_HELP: Record<AccessMode, string> = {
  auto: "The groups this person belongs to decide this terminal.",
  on: "On whatever the groups say, with the hours set here.",
  off: "Off whatever the groups say — an exception for this person.",
  // Two sentences, and the second one is the point: this reads as an exclusion unless it says outright
  // that it is not one (ADR-0016 addendum 2026-09-17, "'not on the terminal' and 'not allowed in' are
  // different sentences"). The way out is named too, because an operator who DOES want this person
  // barred here has one — writing `off` over the hold, which this same row's control still does.
  pass_only:
    "Not sent to this terminal at all: they come through it with a QR pass this platform checks when it is scanned, so the terminal holds nothing for them and spends none of its user slots. It bars nothing — set this row to Off to keep them out of this door.",
};

/** The fields the chip reads; both a person's terminal row and the device page's people row carry them. */
export interface AccessRowFacts {
  mode: AccessMode;
  enabled: boolean;
  /** Names of the groups granting this terminal, sorted; empty for `on`, `off`, `pass_only` and no grant. */
  via: readonly string[];
}

/**
 * The four sentences the plan names (M13 §3): "On · via Sales, Night shift", "On · set here",
 * "Off · set here", "Off · no group grants it". A fifth, "On · follows groups", covers the row the
 * api can hand back for a moment while a recompute is in flight — enabled, following, nothing named
 * yet — so the chip never reads as a contradiction.
 *
 * A sixth since 2026-09-17: **"Pass only · opens with a QR pass"**. It leads with the mode's own word
 * (the ADR settled on `pass_only` precisely because a column whose misreading IS the defect has to be
 * unmisreadable, "and it is the chip the portal shows") and then says the POSITIVE fact, that they get
 * in, because the defect was an operator reading a hold as a bar. Tone `info` and not `neutral`: the
 * two "Off" chips are neutral/hollow, and a chip that shares their shape is read as one of them at a
 * glance — which is the misreading. Not `success` either, since nothing about this row is a grant on
 * THIS terminal; `info` is the tone this codebase already uses for a fact about what kind of thing
 * something is (Trial, Update) rather than a state to act on.
 */
export function accessModePresentation(row: AccessRowFacts): StatePresentation {
  if (row.mode === "on") return { tone: "success", shape: "filled", label: "On · set here" };
  if (row.mode === "off") return { tone: "neutral", shape: "hollow", label: "Off · set here" };
  if (row.mode === "pass_only")
    return { tone: "info", shape: "filled", label: "Pass only · opens with a QR pass" };
  if (!row.enabled) return { tone: "neutral", shape: "hollow", label: "Off · no group grants it" };
  return {
    tone: "success",
    shape: "filled",
    label: row.via.length > 0 ? `On · via ${row.via.join(", ")}` : "On · follows groups",
  };
}

/**
 * The same fact in a sentence, for a tooltip or a line under the pickers.
 *
 * `pass_only` arrives through the same `mode !== "auto"` branch as `on` and `off` — it is a decided
 * row, not a derived one, and `ACCESS_MODE_HELP` now has its sentence. What it must NOT reach is the
 * `!row.enabled` line below: a hold is always `enabled: false`, so that line is exactly where the
 * defect came out on screen.
 */
export function accessViaSentence(row: AccessRowFacts): string {
  if (row.mode !== "auto") return ACCESS_MODE_HELP[row.mode];
  if (!row.enabled) return "No group this person belongs to names this terminal.";
  return row.via.length > 0
    ? `Granted by ${row.via.join(", ")}.`
    : "Granted by a group this person belongs to.";
}
